/**
 * EPBCS Phase 2 — Contract based budgeting (revenue_contract / opex_contract).
 * Spread contract value evenly or manually across the budget window.
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Plus, Trash, FloppyDisk } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Field, Input, Select, Btn } from "./common";
import { PovBar, useDims, dimOptions, fmtMonth, fmtNum } from "./povUtils";
import { ItemComments, useCommentCounts } from "./ItemComments";

const EMPTY = {
  division: "", contract_number: "", description: "", supplier: "",
  start_date: "", end_date: "", currency: "", contract_value: "",
  spread_method: "even", allocations: {},
  method: "manual", baseline: "prior_month", increment_pct: 0,
};

const BASELINE_LABELS = {
  prior_month: "Prior Month", cy_budget: "Current Year Budget",
  cy_actual: "Prior Year Actual", cy_avg: "Average of Current Year Actuals",
};
const BASELINE_OPTIONS = ["prior_month", "cy_budget", "cy_avg", "cy_actual"];

export default function ContractForm({ form }) {
  const acctType = form.startsWith("revenue") ? "Revenue" : "Expense";
  const dims = useDims();
  const [pov, setPov] = useState(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [draft, setDraft] = useState(null);
  const section = form.startsWith("revenue") ? "revenue" : "opex";
  const commentCounts = useCommentCounts(section, pov);

  const contractAccounts = (dims?.account || []).filter(
    (a) => a.attrs?.account_type === acctType && a.attrs?.budget_method === "contract");

  useEffect(() => {
    if (dims && !pov) {
      setPov({
        entity: dims.entity?.[0]?.code || "",
        division: dims.division?.[0]?.code || "",
        account: contractAccounts[0]?.code || "",
        line_of_business: dims.line_of_business?.[0]?.code || "",
        department: dims.department?.[0]?.code || "",
        version: dims.version?.[0]?.code || "",
      });
    }
  }, [dims]); // eslint-disable-line

  const load = () => {
    if (!pov) return;
    setError(null);
    api.post("/epbcs/contracts/list", { form, pov })
      .then((r) => setData(r.data))
      .catch((e) => { setData(null); setError(e?.response?.data?.detail || e.message); });
  };
  useEffect(load, [pov, form]); // eslint-disable-line

  if (!dims || !pov) return <p className="text-sm text-slate-400">Loading masters…</p>;
  if (error) return <p className="text-sm text-red-600" data-testid={`contract-error-${form}`}>{error}</p>;

  const canEdit = data && (data.perm === "input" || data.perm === "full");

  const save = async (c) => {
    try {
      await api.post("/epbcs/contracts", { form, pov, ...c,
        contract_value: +c.contract_value || 0, increment_pct: +c.increment_pct || 0 });
      toast.success("Contract saved");
      setDraft(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save contract");
    }
  };
  const del = async (id) => { await api.delete(`/epbcs/contracts/${id}`); load(); };

  const totals = {};
  (data?.contracts || []).forEach((c) => {
    Object.entries(c.allocations || {}).forEach(([m, v]) => { totals[m] = (totals[m] || 0) + v; });
  });

  return (
    <div className="space-y-4" data-testid={`contract-form-${form}`}>
      <PovBar testid={`pov-${form}`} pov={pov} setPov={setPov} fields={[
        { k: "entity", label: "Entity", options: dimOptions(dims, "entity") },
        { k: "division", label: "Division", options: dimOptions(dims, "division") },
        { k: "account", label: "Account", options: contractAccounts.map((a) => ({ value: a.code, label: `${a.code} — ${a.name}` })) },
        { k: "line_of_business", label: "Line of Business", options: dimOptions(dims, "line_of_business") },
        { k: "department", label: "Department", options: dimOptions(dims, "department") },
        { k: "version", label: "Version", options: dimOptions(dims, "version") },
      ]} />

      {!data ? <p className="text-sm text-slate-400">Loading…</p> : (
        <p className="text-[11px] text-slate-500" data-testid={`contract-forecast-period-${form}`}>
          Forecast Months: <b>{data.forecast_period || "—"}</b> — months designated Calculated use the
          Baseline &amp; Increment % below to auto-project values.
        </p>
      )}

      {canEdit && !draft && (
        <button onClick={() => setDraft({ ...EMPTY, currency: dims.currency?.[0]?.code || "" })}
          data-testid={`new-contract-btn-${form}`}
          className="inline-flex items-center gap-1.5 bg-[#0B1E3F] text-white px-3 py-2 text-[11px] font-bold uppercase tracking-wider rounded">
          <Plus size={13} weight="bold" /> New contract
        </button>
      )}

      {draft && (
        <div className="rounded-sm border border-[#0B1E3F]/30 bg-slate-50 p-4" data-testid={`contract-draft-${form}`}>
          <h4 className="font-bold text-sm mb-3">New contract</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Field label="Division *">
              <Select value={draft.division} data-testid={`draft-cc-${form}`}
                onChange={(e) => setDraft({ ...draft, division: e.target.value })}>
                <option value="">Select…</option>
                {(dims.division || []).map((c) => <option key={c.code} value={c.code}>{c.code} — {c.name}</option>)}
              </Select>
            </Field>
            <Field label="Contract #"><Input data-testid={`draft-number-${form}`} value={draft.contract_number}
              onChange={(e) => setDraft({ ...draft, contract_number: e.target.value })} /></Field>
            <Field label="Description"><Input data-testid={`draft-desc-${form}`} value={draft.description}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })} /></Field>
            <Field label="Supplier"><Input value={draft.supplier}
              onChange={(e) => setDraft({ ...draft, supplier: e.target.value })} /></Field>
            <Field label="Contract Start Date *"><Input type="date" data-testid={`draft-start-${form}`} value={draft.start_date}
              onChange={(e) => setDraft({ ...draft, start_date: e.target.value })} /></Field>
            <Field label="End Date *"><Input type="date" data-testid={`draft-end-${form}`} value={draft.end_date}
              onChange={(e) => setDraft({ ...draft, end_date: e.target.value })} /></Field>
            <Field label="Currency *">
              <Select value={draft.currency} onChange={(e) => setDraft({ ...draft, currency: e.target.value })}>
                {(dims.currency || []).map((c) => <option key={c.code} value={c.code}>{c.code}</option>)}
              </Select>
            </Field>
            <Field label="Type Method *">
              <Select value={draft.method} data-testid={`draft-method-${form}`}
                onChange={(e) => setDraft({ ...draft, method: e.target.value })}>
                <option value="manual">Manual Input</option>
                <option value="calculated">Calculated</option>
              </Select>
            </Field>
            {draft.method === "calculated" ? (
              <>
                <Field label="Baseline Value – Forecasting *">
                  <Select value={draft.baseline} data-testid={`draft-baseline-${form}`}
                    onChange={(e) => setDraft({ ...draft, baseline: e.target.value })}>
                    {BASELINE_OPTIONS.map((b) => <option key={b} value={b}>{BASELINE_LABELS[b]}</option>)}
                  </Select>
                </Field>
                <Field label="Increment % – Forecasting">
                  <Input type="number" data-testid={`draft-increment-${form}`} value={draft.increment_pct}
                    onChange={(e) => setDraft({ ...draft, increment_pct: e.target.value })} />
                </Field>
              </>
            ) : (
              <>
                <Field label="Contract Value *"><Input type="number" min="0" data-testid={`draft-value-${form}`} value={draft.contract_value}
                  onChange={(e) => setDraft({ ...draft, contract_value: e.target.value })} /></Field>
                <Field label="Spread Method *">
                  <Select value={draft.spread_method} data-testid={`draft-spread-${form}`}
                    onChange={(e) => setDraft({ ...draft, spread_method: e.target.value })}>
                    <option value="even">Even Spread</option>
                    <option value="manual">Manual Spread</option>
                  </Select>
                </Field>
              </>
            )}
          </div>
          {draft.method === "manual" && draft.spread_method === "manual" && (() => {
            const s = draft.start_date?.slice(0, 7), e = draft.end_date?.slice(0, 7);
            if (!s || !e) return <p className="mt-3 text-[11px] text-amber-700" data-testid={`contract-needs-dates-${form}`}>Set the Contract Start and End Date to enter monthly values.</p>;
            const cms = (data?.months || []).filter((m) => m >= s && m <= e);
            const tot = cms.reduce((acc, m) => acc + (+draft.allocations?.[m] || 0), 0);
            const target = +draft.contract_value || 0;
            const match = Math.abs(tot - target) <= 0.01;
            return (
              <div className="mt-3">
                <div className="text-[10px] tracking-[0.15em] uppercase font-bold text-slate-500 mb-1.5">
                  Monthly values — only months within the contract period are open · total must equal the contract value
                </div>
                <div className="flex flex-wrap gap-2" data-testid={`contract-manual-months-${form}`}>
                  {cms.map((m) => (
                    <div key={m} className="w-[86px]">
                      <div className="text-[9px] uppercase font-bold text-slate-400 text-center">{fmtMonth(m)}</div>
                      <Input type="number" min="0" data-testid={`contract-alloc-${m}`}
                        value={draft.allocations?.[m] ?? ""}
                        onChange={(ev) => setDraft({ ...draft, allocations: { ...draft.allocations, [m]: ev.target.value } })} />
                    </div>
                  ))}
                </div>
                <p className={`mt-2 text-[11px] font-semibold ${match ? "text-green-700" : "text-red-600"}`}
                  data-testid={`contract-manual-total-${form}`}>
                  Monthly total: {fmtNum(tot)} / Contract value: {fmtNum(target)}{match ? " ✓" : " — must match before saving"}
                </p>
              </div>
            );
          })()}
          <div className="mt-3 flex gap-2 justify-end">
            <button onClick={() => setDraft(null)} className="border border-slate-300 text-slate-600 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider rounded">Cancel</button>
            <Btn onClick={() => save(draft)} data-testid={`draft-save-${form}`}
              disabled={!draft.division || !draft.contract_number || !draft.start_date || !draft.end_date ||
                !draft.currency || (draft.method === "manual" && !draft.contract_value)}>
              Create contract
            </Btn>
          </div>
        </div>
      )}

      {!data ? <p className="text-sm text-slate-400">Loading…</p> : (
        <div className="rounded-sm border border-slate-200 bg-white shadow-sm overflow-x-auto">
          <table className="text-[12px] w-full">
            <thead>
              <tr className="text-left text-[9px] tracking-[0.12em] uppercase text-slate-500 border-b border-slate-200 bg-slate-50">
                <th className="py-2 px-3 min-w-[110px] sticky left-0 bg-slate-50 z-10">Contract #</th>
                <th className="py-2 px-2 min-w-[140px]">Description</th>
                <th className="py-2 px-2 min-w-[100px]">Supplier</th>
                <th className="py-2 px-2 min-w-[90px]">Division</th>
                <th className="py-2 px-2 min-w-[95px]">Start</th>
                <th className="py-2 px-2 min-w-[95px]">End</th>
                <th className="py-2 px-2">Ccy</th>
                <th className="py-2 px-2 min-w-[85px]">Method</th>
                <th className="py-2 px-2 min-w-[110px]">Baseline</th>
                <th className="py-2 px-2 text-right min-w-[70px]">Incr %</th>
                <th className="py-2 px-2 text-right min-w-[90px]">Value</th>
                <th className="py-2 px-2 min-w-[80px]">Spread</th>
                {data.months.map((m) => <th key={m} className="py-2 px-2 text-right min-w-[76px] whitespace-nowrap">{fmtMonth(m)}</th>)}
                {canEdit && <th className="py-2 px-2 w-16" />}
              </tr>
            </thead>
            <tbody>
              {data.contracts.map((c) => (
                <ContractRow key={c.id} c={c} months={data.months} canEdit={canEdit}
                  form={form} onSave={save} onDelete={del}
                  comments={<ItemComments section={section} pov={pov} itemType="contract"
                    itemKey={c.contract_number} itemLabel={`Contract ${c.contract_number} — ${c.description || c.supplier || ""}`}
                    count={commentCounts.counts[c.contract_number] || 0} onChanged={commentCounts.refresh} />} />
              ))}
              {data.contracts.length === 0 && (
                <tr><td colSpan={13 + data.months.length} className="py-6 text-center text-slate-400">
                  No contracts for this POV yet.{canEdit ? " Create one above." : ""}
                </td></tr>
              )}
            </tbody>
            {data.contracts.length > 0 && (
              <tfoot>
                <tr className="border-t-2 border-slate-300 bg-slate-50 font-bold">
                  <td className="py-2 px-3 sticky left-0 bg-slate-50 z-10" colSpan={10}>Total</td>
                  <td className="py-2 px-2 text-right" data-testid={`contract-total-value-${form}`}>
                    {fmtNum(data.contracts.reduce((s, c) => s + (c.contract_value || 0), 0))}
                  </td>
                  <td />
                  {data.months.map((m) => <td key={m} className="py-2 px-2 text-right whitespace-nowrap">{fmtNum(totals[m] || 0)}</td>)}
                  {canEdit && <td />}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </div>
  );
}

const ContractRow = ({ c, months, canEdit, form, onSave, onDelete, comments }) => {
  const [row, setRow] = useState(c);
  const [dirty, setDirty] = useState(false);
  useEffect(() => { setRow(c); setDirty(false); }, [c]);
  const upd = (k, v) => { setRow({ ...row, [k]: v }); setDirty(true); };
  const updAlloc = (m, v) => { setRow({ ...row, allocations: { ...row.allocations, [m]: +v || 0 } }); setDirty(true); };
  const manual = row.method !== "calculated" && row.spread_method === "manual";
  const cell = "border border-slate-200 px-1 py-0.5 text-[11px] rounded w-full";
  return (
    <tr className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`contract-row-${form}-${c.contract_number}`}>
      <td className="py-1.5 px-3 sticky left-0 bg-white z-10 font-mono font-semibold whitespace-nowrap">{row.contract_number}</td>
      <td className="py-1 px-1">{canEdit ? <input className={cell} value={row.description} onChange={(e) => upd("description", e.target.value)} /> : row.description}</td>
      <td className="py-1 px-1">{canEdit ? <input className={cell} value={row.supplier} onChange={(e) => upd("supplier", e.target.value)} /> : row.supplier}</td>
      <td className="py-1.5 px-2 font-mono">{row.division}</td>
      <td className="py-1 px-1">{canEdit ? <input type="date" className={cell} value={row.start_date} onChange={(e) => upd("start_date", e.target.value)} /> : row.start_date}</td>
      <td className="py-1 px-1">{canEdit ? <input type="date" className={cell} value={row.end_date} onChange={(e) => upd("end_date", e.target.value)} /> : row.end_date}</td>
      <td className="py-1.5 px-2">{row.currency}</td>
      <td className="py-1 px-1">
        {canEdit ? (
          <select className={`${cell} bg-white`} data-testid={`contract-method-${form}-${c.contract_number}`}
            value={row.method || "manual"} onChange={(e) => upd("method", e.target.value)}>
            <option value="manual">Manual Input</option>
            <option value="calculated">Calculated</option>
          </select>
        ) : (row.method === "calculated" ? "Calculated" : "Manual Input")}
      </td>
      <td className="py-1 px-1">
        {row.method === "calculated" ? (
          canEdit ? (
            <select className={`${cell} bg-white`} data-testid={`contract-baseline-${form}-${c.contract_number}`}
              value={row.baseline || "prior_month"} onChange={(e) => upd("baseline", e.target.value)}>
              {BASELINE_OPTIONS.map((b) => <option key={b} value={b}>{BASELINE_LABELS[b]}</option>)}
            </select>
          ) : BASELINE_LABELS[row.baseline] || row.baseline
        ) : "—"}
      </td>
      <td className="py-1 px-1 text-right">
        {row.method === "calculated" ? (
          canEdit ? (
            <input type="number" className={`${cell} text-right`} data-testid={`contract-increment-${form}-${c.contract_number}`}
              value={row.increment_pct ?? 0} onChange={(e) => upd("increment_pct", +e.target.value || 0)} />
          ) : `${row.increment_pct || 0}%`
        ) : "—"}
      </td>
      <td className="py-1 px-1">
        {row.method === "calculated" ? (
          <span className="text-slate-500" data-testid={`contract-value-${form}-${c.contract_number}`}>{fmtNum(row.contract_value)} (calc)</span>
        ) : canEdit ? <input type="number" className={`${cell} text-right`} data-testid={`contract-value-${form}-${c.contract_number}`}
          value={row.contract_value} onChange={(e) => upd("contract_value", +e.target.value || 0)} /> : fmtNum(row.contract_value)}
      </td>
      <td className="py-1 px-1">
        {row.method === "calculated" ? "—" : canEdit ? (
          <select className={`${cell} bg-white`} value={row.spread_method} onChange={(e) => upd("spread_method", e.target.value)}>
            <option value="even">Even</option><option value="manual">Manual</option>
          </select>
        ) : row.spread_method}
      </td>
      {months.map((m) => {
        const inPeriod = m >= (row.start_date || "").slice(0, 7) && m <= (row.end_date || "9999").slice(0, 7);
        return (
        <td key={m} className="py-1 px-1 text-right">
          {canEdit && manual && inPeriod ? (
            <input type="number" min="0" className={`${cell} text-right w-[72px]`}
              value={row.allocations?.[m] ?? ""} onChange={(e) => updAlloc(m, e.target.value)} />
          ) : (
            <span className={`whitespace-nowrap ${row.allocations?.[m] ? "text-[#0B1E3F] font-medium" : "text-slate-300"}`}>
              {fmtNum(row.allocations?.[m] || 0)}
            </span>
          )}
        </td>
        );
      })}
      {canEdit && (
        <td className="py-1.5 px-2 whitespace-nowrap">
          <button onClick={() => onSave(row)} disabled={!dirty} title="Save"
            data-testid={`contract-save-${form}-${c.contract_number}`}
            className={`mr-1.5 ${dirty ? "text-[#0B1E3F]" : "text-slate-300"}`}><FloppyDisk size={15} weight="bold" /></button>
          <button onClick={() => onDelete(c.id)} title="Delete"
            data-testid={`contract-delete-${form}-${c.contract_number}`}
            className="text-slate-400 hover:text-red-600"><Trash size={14} /></button>
        </td>
      )}
    </tr>
  );
};
